import { ChevronRight, Megaphone } from 'lucide-react'
import { useFeaturebase } from 'featurebase-js/react'
import { useUnreadChangelog } from '../../hooks/useUnreadChangelog'
import { useFeaturebaseChangelog } from '../../lib/featurebase-context'
import type { FeaturebaseChangelogApi } from '../../lib/featurebase-context'
import { formatBadgeCount } from '../../utils/badgeCount'

// Featurebase closes its updates popup whenever the messenger panel is open,
// so the messenger is closed first and the popup opened once it has gone.
const OPEN_POPUP_AFTER_MESSENGER_CLOSE_MS = 300

/**
 * Home "Recent updates" button. Always visible wherever Featurebase is
 * enabled; tapping it opens Featurebase's full-screen updates popup.
 *
 * A secondary shortcut for browsing every update (9-Oct-26 call): the main
 * prompt for new updates is the count on the header help icon.
 *
 * A megaphone icon (not sparkles, which read as an AI feature — 9-Oct-26
 * call) and a count of unread updates, capped at "99+". Read state is
 * Featurebase's and lives per browser, so the count is only a hint — the
 * button works the same either way. Tapping marks everything read at once
 * (the popup lists every update regardless) and clears the count.
 *
 * Renders nothing where Featurebase is switched off or not ready yet.
 */
export function WhatsNewButton() {
  // Only set beneath the enabled Provider, so the inner button's messenger
  // hook never runs where Featurebase is off.
  const changelog = useFeaturebaseChangelog()
  if (!changelog) {
    return null
  }
  return <WhatsNewButtonInner changelog={changelog} />
}

interface WhatsNewButtonInnerProps {
  changelog: FeaturebaseChangelogApi
}

function WhatsNewButtonInner({ changelog }: WhatsNewButtonInnerProps) {
  const { hide: hideMessenger } = useFeaturebase()
  const { hasUnreadUpdates: hasUnread, unreadUpdatesCount, markUpdatesRead } =
    useUnreadChangelog()
  const countLabel = formatBadgeCount(unreadUpdatesCount)

  const handleClick = () => {
    markUpdatesRead()
    // If the messenger is open, Featurebase would shut the popup at once.
    hideMessenger()
    window.setTimeout(
      () => changelog.openPopup(),
      OPEN_POPUP_AFTER_MESSENGER_CLOSE_MS,
    )
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      // Filled with the tenant's brand colour so it stands out (9-Oct-26
      // call). --brand-primary-text is the readable text colour picked for
      // that brand, the same pairing as the primary Button.
      className="w-full min-h-[52px] flex items-center gap-3 px-4 py-3 mb-4 bg-brand-primary text-[var(--brand-primary-text)] rounded-2xl shadow-md text-left cursor-pointer transition-transform hover:bg-brand-primary-hover active:scale-[0.99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      aria-label={hasUnread ? `Recent updates. ${countLabel} unread.` : 'Recent updates'}
    >
      <span
        // Soft circle behind the icon, in the brand's text colour at low
        // opacity, so the megaphone reads as a badge rather than a glyph.
        className="inline-flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0"
        style={{ backgroundColor: 'color-mix(in srgb, var(--brand-primary-text) 18%, transparent)' }}
        aria-hidden="true"
      >
        <Megaphone className="w-5 h-5" />
      </span>
      <span className="flex-1 text-[15px] font-semibold">
        Recent updates
      </span>
      {hasUnread && (
        <span
          // Inverted pill (brand text colour behind brand-coloured digits) so
          // the count pops against the filled button.
          className="inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 rounded-full bg-[var(--brand-primary-text)] text-brand-primary text-xs font-bold"
          aria-hidden="true"
        >
          {countLabel}
        </span>
      )}
      <ChevronRight className="w-5 h-5 flex-shrink-0 opacity-80" aria-hidden="true" />
    </button>
  )
}
